/* eslint-disable @typescript-eslint/no-explicit-any */
// Roadmap Toolkit - Supabase Client
// Shared client for all roadmap apps

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type {
    Roadmap,
    UserStoryMap,
    MetroLine,
    MetroStation,
    MetroConnection,
    UsmActivity,
    UsmStep,
    UsmRelease,
    UsmStory,
    CanvasNode,
    CanvasEdge,
    GnlColumn,
    GnlItem,
    FuncGoal,
    FuncFeature,
    FuncMilestone,
    GdObjective,
    GdInitiative,
} from './types';

// Helper for typed queries

const from = <T>(table: keyof Database['public']['Tables']) => supabase.from(table) as unknown as { select: (query?: string) => { eq: (column: string, value: unknown) => any, in: (column: string, values: unknown[]) => any, or: (filters: string) => any, order: (column: string, options?: unknown) => any, single: () => Promise<{ data: T | null, error: any }> }, insert: (data: unknown) => any, update: (data: unknown) => any, delete: () => any };

// Retry helper for AbortError (common in React StrictMode)
const withRetry = async <T>(
    queryFn: () => Promise<{ data: T | null; error: any }>,
    maxRetries = 2
): Promise<{ data: T | null; error: any }> => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        const result = await queryFn();

        // If successful or non-abort error, return
        if (!result.error || !result.error.message?.includes('abort')) {
            return result;
        }

        // On abort, wait briefly before retry
        if (attempt < maxRetries) {
            await new Promise(r => setTimeout(r, 100 * (attempt + 1)));
        }
    }
    // Return last result after all retries
    return queryFn();
};

// =============================================================================
// USER STORY MAPS (Core)
// =============================================================================
export const storyMapsApi = {
    list: () =>
        withRetry(async () => await supabase.from('user_story_maps' as any)
            .select('*')
            .eq('is_archived', false)
            .order('created_at', { ascending: false }) as any as Promise<{ data: UserStoryMap[] | null; error: any }>),

    get: (id: string) =>
        supabase.from('user_story_maps' as any).select('*').eq('id', id).single() as any as Promise<{ data: UserStoryMap | null; error: any }>,

    create: (data: Pick<UserStoryMap, 'owner_id' | 'name'> & { description?: string }) =>
        supabase.from('user_story_maps' as any).insert(data).select().single() as any as Promise<{ data: UserStoryMap | null; error: any }>,

    update: (id: string, data: Partial<Pick<UserStoryMap, 'name' | 'description'>>) =>
        supabase.from('user_story_maps' as any).update(data).eq('id', id).select().single() as any as Promise<{ data: UserStoryMap | null; error: any }>,

    delete: (id: string) =>
        supabase.from('user_story_maps' as any).delete().eq('id', id),
};

// =============================================================================
// ROADMAPS (Core)
// =============================================================================
export const roadmapsApi = {
    list: () =>
        withRetry(async () => await supabase.from('roadmaps')
            .select('*')
            .eq('is_archived', false)
            .order('created_at', { ascending: false }).returns<Roadmap[]>()),

    get: (id: string) =>
        from<Roadmap>('roadmaps').select('*').eq('id', id).single(),

    create: (data: Pick<Roadmap, 'owner_id' | 'name' | 'description'>) =>
        from<Roadmap>('roadmaps').insert(data).select().single(),

    update: (id: string, data: Partial<Roadmap>) =>
        from<Roadmap>('roadmaps').update(data).eq('id', id).select().single(),

    delete: (id: string) =>
        from<Roadmap>('roadmaps').delete().eq('id', id),

    // Collaborators
    getCollaborators: (roadmapId: string) =>
        supabase.rpc('get_roadmap_collaborators' as any, { p_roadmap_id: roadmapId }),

    addCollaborator: (roadmapId: string, email: string, role: string) =>
        supabase.rpc('add_roadmap_collaborator' as any, { p_roadmap_id: roadmapId, p_email: email, p_role: role }),
};

// =============================================================================
// METRO ROADMAP
// =============================================================================
export const metroApi = {
    // Lines
    listLines: (roadmapId: string) =>
        withRetry(async () => await supabase.from('roadmap_metro_lines')
            .select('*')
            .eq('roadmap_id', roadmapId)
            .eq('is_archived', false)
            .order('order_index').returns<MetroLine[]>()),

    createLine: (data: Pick<MetroLine, 'owner_id' | 'roadmap_id' | 'name' | 'color'> & Partial<Pick<MetroLine, 'order_index'>>) =>
        from<MetroLine>('roadmap_metro_lines').insert(data).select().single(),

    updateLine: (id: string, data: Partial<MetroLine>) =>
        from<MetroLine>('roadmap_metro_lines').update(data).eq('id', id).select().single(),

    deleteLine: (id: string) =>
        from<MetroLine>('roadmap_metro_lines').delete().eq('id', id),

    // Stations
    listStations: (lineId: string) =>
        supabase.from('roadmap_metro_stations')
            .select('*')
            .eq('line_id', lineId)
            .eq('is_archived', false)
            .order('year')
            .order('month_index').returns<MetroStation[]>(),

    createStation: (data: Pick<MetroStation, 'line_id' | 'year' | 'month_index' | 'name'>) =>
        from<MetroStation>('roadmap_metro_stations').insert(data).select().single(),

    updateStation: (id: string, data: Partial<MetroStation>) =>
        from<MetroStation>('roadmap_metro_stations').update(data).eq('id', id).select().single(),

    deleteStation: (id: string) =>
        from<MetroStation>('roadmap_metro_stations').delete().eq('id', id),

    // Connections
    listConnections: (stationIds: string[]) =>
        supabase.from('roadmap_metro_connections')
            .select('*')
            .or(`source_station_id.in.(${stationIds.join(',')}),target_station_id.in.(${stationIds.join(',')})`).returns<MetroConnection[]>(),

    createConnection: (data: Pick<MetroConnection, 'source_station_id' | 'target_station_id' | 'curve_type'>) =>
        from<MetroConnection>('roadmap_metro_connections').insert(data).select().single(),

    deleteConnection: (id: string) =>
        from<MetroConnection>('roadmap_metro_connections').delete().eq('id', id),
};

// =============================================================================
// USER STORY MAP
// =============================================================================
export const usmApi = {
    // Full story map data — scoped by story_map_id
    getFullMap: async (storyMapId: string) => {
        return withRetry(async () => {
            // Fetch activities for this story map
            const activitiesRes = await from<UsmActivity>('roadmap_usm_activities')
                .select('*')
                .eq('story_map_id', storyMapId)
                .eq('is_archived', false)
                .order('order_index');

            // Fetch releases for this story map
            const releasesRes = await from<UsmRelease>('roadmap_usm_releases')
                .select('*')
                .eq('story_map_id', storyMapId)
                .eq('is_archived', false)
                .order('order_index');

            if (activitiesRes.error) return { data: null, error: activitiesRes.error };
            if (releasesRes.error) return { data: null, error: releasesRes.error };

            const activities = activitiesRes.data || [];
            const releases = releasesRes.data || [];

            // If no activities, we can return early, but we still need releases.

            // Steps need to filter by activities.
            const activityIds = activities.map((a: UsmActivity) => a.id);
            let steps: UsmStep[] = [];
            let stories: UsmStory[] = [];

            if (activityIds.length > 0) {
                const stepsRes = await supabase.from('roadmap_usm_steps')
                    .select('*')
                    .in('activity_id', activityIds)
                    .eq('is_archived', false)
                    .order('order_index');

                if (stepsRes.error) return { data: null, error: stepsRes.error };
                steps = stepsRes.data as UsmStep[];

                const stepIds = steps.map(s => s.id);
                if (stepIds.length > 0) {
                    const storiesRes = await supabase.from('roadmap_usm_stories')
                        .select('*')
                        .in('step_id', stepIds)
                        .eq('is_archived', false);
                    if (storiesRes.error) return { data: null, error: storiesRes.error };
                    stories = storiesRes.data as UsmStory[];
                }
            }

            return { data: { activities, steps, releases, stories } as unknown as any, error: null };
        });
    },

    createActivity: (data: Pick<UsmActivity, 'owner_id' | 'story_map_id' | 'title' | 'order_index'>) =>
        from<UsmActivity>('roadmap_usm_activities').insert(data).select().single(),

    createStep: (data: Pick<UsmStep, 'activity_id' | 'title' | 'order_index'>) =>
        from<UsmStep>('roadmap_usm_steps').insert(data).select().single(),

    createRelease: (data: Pick<UsmRelease, 'owner_id' | 'story_map_id' | 'name' | 'order_index'> & { target_date?: string }) =>
        from<UsmRelease>('roadmap_usm_releases').insert(data).select().single(),

    createStory: (data: Pick<UsmStory, 'step_id' | 'title'> & Partial<Pick<UsmStory, 'release_id' | 'priority'>>) =>
        from<UsmStory>('roadmap_usm_stories').insert(data).select().single(),

    updateStory: (id: string, data: Partial<UsmStory>) =>
        from<UsmStory>('roadmap_usm_stories').update(data).eq('id', id).select().single(),

    moveStory: (id: string, releaseId: string | null, stepId?: string) =>
        from<UsmStory>('roadmap_usm_stories')
            .update({ release_id: releaseId, ...(stepId && { step_id: stepId }) })
            .eq('id', id)
            .select()
            .single(),

    deleteStory: (id: string) =>
        from<UsmStory>('roadmap_usm_stories').delete().eq('id', id),

    deleteActivity: (id: string) =>
        from<UsmActivity>('roadmap_usm_activities').delete().eq('id', id),

    deleteStep: (id: string) =>
        from<UsmStep>('roadmap_usm_steps').delete().eq('id', id),

    deleteRelease: (id: string) =>
        from<UsmRelease>('roadmap_usm_releases').delete().eq('id', id),

    updateActivity: (id: string, data: Partial<UsmActivity>) =>
        from<UsmActivity>('roadmap_usm_activities').update(data).eq('id', id).select().single(),

    updateStep: (id: string, data: Partial<UsmStep>) =>
        from<UsmStep>('roadmap_usm_steps').update(data).eq('id', id).select().single(),

    updateRelease: (id: string, data: Partial<UsmRelease>) =>
        from<UsmRelease>('roadmap_usm_releases').update(data).eq('id', id).select().single(),
};

// =============================================================================
// BOLD STEPS CANVAS
// =============================================================================
export const canvasApi = {
    listNodes: (roadmapId: string) =>
        withRetry(async () => await supabase.from('roadmap_canvas_nodes')
            .select('*')
            .eq('roadmap_id', roadmapId)
            .eq('is_archived', false).returns<CanvasNode[]>()),

    createNode: (data: Pick<CanvasNode, 'owner_id' | 'roadmap_id' | 'type' | 'content' | 'position_x' | 'position_y'>) =>
        from<CanvasNode>('roadmap_canvas_nodes').insert(data).select().single(),

    updateNode: (id: string, data: Partial<CanvasNode>) =>
        from<CanvasNode>('roadmap_canvas_nodes').update(data).eq('id', id).select().single(),

    deleteNode: (id: string) =>
        from<CanvasNode>('roadmap_canvas_nodes').delete().eq('id', id),

    // Edges
    listEdges: (roadmapId: string) =>
        withRetry(async () => await supabase.from('roadmap_canvas_edges')
            .select('*')
            .eq('roadmap_id', roadmapId).returns<CanvasEdge[]>()),

    createEdge: (data: Pick<CanvasEdge, 'owner_id' | 'roadmap_id' | 'source_id' | 'target_id' | 'label'>) =>
        from<CanvasEdge>('roadmap_canvas_edges').insert(data).select().single(),

    deleteEdge: (id: string) =>
        from<CanvasEdge>('roadmap_canvas_edges').delete().eq('id', id),

    clearCanvas: (roadmapId: string) =>
        from<CanvasNode>('roadmap_canvas_nodes').delete().eq('roadmap_id', roadmapId),
};

// =============================================================================
// GOAL-ORIENTED (NOW-NEXT-LATER)
// =============================================================================
export const gnlApi = {
    getBoard: async (roadmapId: string) => {
        return withRetry(async () => {
            const columns = await from<GnlColumn>('roadmap_gnl_columns')
                .select('*')
                .eq('roadmap_id', roadmapId)
                .order('order_index');

            if (columns.error) return { data: null, error: columns.error };
            if (!columns.data || columns.data.length === 0) return { data: { columns: { data: [] }, items: { data: [] } } as unknown as any, error: null };

            const items = await from<GnlItem>('roadmap_gnl_items')
                .select('*')
                .in('column_id', columns.data.map((c: GnlColumn) => c.id))
                .eq('is_archived', false)
                .order('order_index');

            if (items.error) return { data: null, error: items.error };
            return { data: { columns, items } as unknown as any, error: null };
        });
    },

    initializeColumns: async (ownerId: string, roadmapId: string) => {
        const columns: Pick<GnlColumn, 'owner_id' | 'roadmap_id' | 'title' | 'order_index'>[] = [
            { owner_id: ownerId, roadmap_id: roadmapId, title: 'Now', order_index: 0 },
            { owner_id: ownerId, roadmap_id: roadmapId, title: 'Next', order_index: 1 },
            { owner_id: ownerId, roadmap_id: roadmapId, title: 'Later', order_index: 2 },
        ];
        return from<GnlColumn>('roadmap_gnl_columns').insert(columns).select();
    },

    createItem: (data: Pick<GnlItem, 'column_id' | 'content'>) =>
        from<GnlItem>('roadmap_gnl_items').insert(data).select().single(),

    updateItem: (id: string, data: Partial<GnlItem>) =>
        from<GnlItem>('roadmap_gnl_items').update(data).eq('id', id).select().single(),

    moveItem: (id: string, columnId: string, orderIndex: number) =>
        from<GnlItem>('roadmap_gnl_items').update({ column_id: columnId, order_index: orderIndex }).eq('id', id).select().single(),

    // Column Management
    createColumn: (data: Pick<GnlColumn, 'owner_id' | 'roadmap_id' | 'title' | 'order_index'>) =>
        from<GnlColumn>('roadmap_gnl_columns').insert(data).select().single(),

    updateColumn: (id: string, data: Partial<GnlColumn>) =>
        from<GnlColumn>('roadmap_gnl_columns').update(data).eq('id', id).select().single(),

    deleteColumn: (id: string) =>
        from<GnlColumn>('roadmap_gnl_columns').delete().eq('id', id),

    deleteItem: (id: string) =>
        from<GnlItem>('roadmap_gnl_items').delete().eq('id', id),
};

// =============================================================================
// FUNCTIONAL ROADMAP
// =============================================================================
export const funcApi = {
    listGoals: (roadmapId: string) =>
        withRetry(async () => await supabase.from('roadmap_func_goals')
            .select('*')
            .eq('roadmap_id', roadmapId)
            .eq('is_archived', false)
            .order('order_index').returns<FuncGoal[]>()),

    getGoalWithFeatures: async (goalId: string) => {
        const goal = await from<FuncGoal>('roadmap_func_goals').select('*').eq('id', goalId).single();
        const features = await supabase.from('roadmap_func_features').select('*').eq('goal_id', goalId).eq('is_archived', false).order('order_index').returns<FuncFeature[]>();
        return { goal, features };
    },

    createGoal: (data: Pick<FuncGoal, 'owner_id' | 'roadmap_id' | 'title'>) =>
        from<FuncGoal>('roadmap_func_goals').insert(data).select().single(),

    createFeature: (data: Pick<FuncFeature, 'goal_id' | 'title'>) =>
        from<FuncFeature>('roadmap_func_features').insert(data).select().single(),

    createMilestone: (data: Pick<FuncMilestone, 'feature_id' | 'title'> & { due_date?: string }) =>
        from<FuncMilestone>('roadmap_func_milestones').insert(data).select().single(),

    listMilestones: (featureId: string) =>
        supabase.from('roadmap_func_milestones')
            .select('*')
            .eq('feature_id', featureId)
            .eq('is_archived', false)
            .order('due_date')
            .returns<FuncMilestone[]>(),

    updateFeature: (id: string, data: Partial<FuncFeature>) =>
        from<FuncFeature>('roadmap_func_features').update(data).eq('id', id).select().single(),

    updateGoal: (id: string, data: Partial<FuncGoal>) =>
        from<FuncGoal>('roadmap_func_goals').update(data).eq('id', id).select().single(),

    updateMilestone: (id: string, data: Partial<FuncMilestone>) =>
        from<FuncMilestone>('roadmap_func_milestones').update(data).eq('id', id).select().single(),

    deleteGoal: (id: string) =>
        from<FuncGoal>('roadmap_func_goals').delete().eq('id', id),

    deleteFeature: (id: string) =>
        from<FuncFeature>('roadmap_func_features').delete().eq('id', id),

    deleteMilestone: (id: string) =>
        from<FuncMilestone>('roadmap_func_milestones').delete().eq('id', id),
};

// =============================================================================
// GOAL-DRIVEN ROADMAP
// =============================================================================
export const gdApi = {
    listObjectives: (roadmapId: string) =>
        withRetry(async () => await supabase.from('roadmap_gd_objectives')
            .select('*')
            .eq('roadmap_id', roadmapId)
            .eq('is_archived', false)
            .order('order_index').returns<GdObjective[]>()),

    getObjectiveWithInitiatives: async (objectiveId: string) => {
        const objective = await from<GdObjective>('roadmap_gd_objectives').select('*').eq('id', objectiveId).single();
        const initiatives = await from<GdInitiative>('roadmap_gd_initiatives').select('*').eq('objective_id', objectiveId).eq('is_archived', false).order('order_index');
        return { objective, initiatives };
    },

    createObjective: (data: Pick<GdObjective, 'owner_id' | 'roadmap_id' | 'title'> & Partial<Pick<GdObjective, 'key_result' | 'target_quarter' | 'target_year'>>) =>
        from<GdObjective>('roadmap_gd_objectives').insert(data).select().single(),

    createInitiative: (data: Pick<GdInitiative, 'objective_id' | 'title'>) =>
        from<GdInitiative>('roadmap_gd_initiatives').insert(data).select().single(),

    updateProgress: (id: string, progress: number) =>
        from<GdObjective>('roadmap_gd_objectives').update({ progress }).eq('id', id).select().single(),

    updateObjective: (id: string, data: Partial<GdObjective>) =>
        from<GdObjective>('roadmap_gd_objectives').update(data).eq('id', id).select().single(),

    deleteObjective: (id: string) =>
        from<GdObjective>('roadmap_gd_objectives').delete().eq('id', id),

    deleteInitiative: (id: string) =>
        from<GdInitiative>('roadmap_gd_initiatives').delete().eq('id', id),
};
